import React, { useState, useEffect } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress
} from '@mui/material';
import { HourglassEmpty as PendingIcon } from '@mui/icons-material';
import { progressStorageService } from '../../../services/progressStorageService';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { formatDate } from '../../../utils/dateFormatter';

interface FormEntry {
  _id?: string;
  sessionId: string;
  customerId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  createdAt?: string;
  lastUpdated?: string;
  currentStep?: number;
  stepName?: string;
  isComplete?: boolean;
  formData?: any;
}

interface IncompleteSubmissionSelectorProps {
  onSelectSubmission: (submission: FormEntry | null) => void;
  selectedSubmission: FormEntry | null;
}

/**
 * Incomplete Submission Selector
 *
 * Dropdown/Autocomplete for selecting an incomplete submission to finish.
 * Shows client name, last updated date, and progress.
 */
const IncompleteSubmissionSelector: React.FC<IncompleteSubmissionSelectorProps> = ({
  onSelectSubmission,
  selectedSubmission
}) => {
  const { customerIds, isAdmin, isDashboardAdmin } = useUserAccess();
  const { token } = useAuth0Token();
  const [submissions, setSubmissions] = useState<FormEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchIncompleteSubmissions();
  }, [token, customerIds]);

  const fetchIncompleteSubmissions = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const incompleteForms = await progressStorageService.getIncompleteSessions(token);

      // Filter by access rights if not admin
      const accessibleForms = (isAdmin || isDashboardAdmin)
        ? incompleteForms
        : incompleteForms.filter(form => {
            const formCustomerId = form.customerId || form.formData?.customerId;
            return formCustomerId && customerIds.includes(String(formCustomerId));
          });

      // Sort by last updated (most recent first)
      accessibleForms.sort((a, b) => {
        const dateA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
        const dateB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
        return dateB - dateA;
      });

      setSubmissions(accessibleForms);
    } catch (err: any) {
      console.error('Error fetching incomplete submissions:', err);
      setError(err.message || 'Failed to load incomplete submissions');
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (form: FormEntry): string => {
    const firstName = form.formData?.personal?.client1?.firstName || form.firstName || '';
    const lastName = form.formData?.personal?.client1?.lastName || form.lastName || '';
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return 'Unknown Client';
  };

  const getTotalSteps = (form: FormEntry): number => {
    return form.formData?.wizardMode === 'basic' ? 5 : 8;
  };

  const getProgressPercentage = (form: FormEntry): number => {
    const currentStep = form.currentStep || 0;
    return Math.round((currentStep / getTotalSteps(form)) * 100);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (submissions.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>No Incomplete Submissions Available</strong>
        </Typography>
        <Typography variant="caption">
          There are no incomplete submissions to finish. Clients will need to start the external webform to create an incomplete submission.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Select an incomplete submission to finish. The form will be pre-filled with the data submitted so far.
      </Typography>

      <Autocomplete
        options={submissions}
        value={selectedSubmission}
        onChange={(_, newValue) => onSelectSubmission(newValue)}
        getOptionLabel={(option) => getClientName(option)}
        isOptionEqualToValue={(option, value) => option.sessionId === value.sessionId}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Incomplete Submission"
            placeholder="Start typing client name..."
            required
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.sessionId}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PendingIcon color="warning" fontSize="small" />
                <Typography variant="body1">
                  {getClientName(option)}
                </Typography>
                <Chip
                  label={`Step ${option.currentStep || 0} of ${getTotalSteps(option)}`}
                  size="small"
                  color="warning"
                />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                Last Updated: {option.lastUpdated ? formatDate(option.lastUpdated) : 'Unknown'}
                {option.stepName && ` • ${option.stepName}`}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={getProgressPercentage(option)}
                  sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {getProgressPercentage(option)}%
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default IncompleteSubmissionSelector;
